import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

import { getAllActivePricingTiers, deletePricingTierById } from '../utils/api/pricingTier';
import { convertToIndianTimeZone } from '../utils/utils';
import AddPricingTierModal from './child/AddPricingTierModal';
import ViewPricingTierModal from './child/ViewPricingTierModal';
import EditPricingTierModal from './child/EditPricingTierModel';

function PricingTierLayer() {
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  const fetchPricingTiers = async () => {
    setLoading(true);
    try {
      const response = await getAllActivePricingTiers();
      const tiers = response || [];
      setPricingTiers(tiers);
    } catch (error) {
      console.error("Error fetching pricing tiers:", error);
      toast.error(error?.message || "Failed to load pricing tiers.");
      setPricingTiers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricingTiers();
  }, []);

  const handleDelete = async (id) => {
    try {
      const res = await deletePricingTierById(id);
      toast.success(res?.message || "Pricing tier deleted");
      fetchPricingTiers();
    } catch (err) {
      toast.error(err?.message || "Failed to delete pricing tier");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="card h-100 p-0 radius-12"
    >
      {/* Header */}
      <div className="card-header border-bottom bg-base py-16 px-24">
        <div className="d-flex align-items-center flex-wrap w-100" style={{ gap: '12px', justifyContent: 'end' }}>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add New Pricing Tier
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th className="text-center">Label</th>
                <th className="text-center">Minutes Range</th>
                <th className="text-center">Rate / Minute (₹)</th>
                <th className="text-center">Status</th>
                <th className="text-center">Created At</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`shimmer-${idx}`}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <td key={i}><div className="shimmer shimmer-text" /></td>
                    ))}
                  </tr>
                ))
              ) : pricingTiers.length > 0 ? (
                pricingTiers.map((tier) => (
                  <tr key={tier.id}>
                    <td className="text-center">{tier.label || '—'}</td>
                    <td className="text-center">{tier.minMinutes} - {tier.maxMinutes} min</td>
                    <td className="text-center">₹{parseFloat(tier.ratePerMinute).toFixed(2)}</td>
                    <td className="text-center">
                      <span className={`badge ${tier.isActive ? 'bg-success' : 'bg-danger'}`}>
                        {tier.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-center">
                      {tier.createdAt ? convertToIndianTimeZone(tier.createdAt) : '—'}
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          onClick={() => {
                            setSelectedTierId(tier.id);
                            setShowViewModal(true);
                          }}
                          className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="majesticons:eye-line" className="icon text-xl" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedTierId(tier.id);
                            setShowEditModal(true);
                          }}
                          className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>
                        <button
                          onClick={() => handleDelete(tier.id)}
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                           <Icon icon="ic:outline-block" className="menu-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '40vh' }}>
                      <Icon icon="icon-park-outline:file-search" className="text-3xl mb-3 text-muted" />
                      <p className="text-secondary-light text-md mb-0">No pricing tiers found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <AddPricingTierModal
        show={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          fetchPricingTiers();
        }}
      />
      <ViewPricingTierModal
        id={selectedTierId}
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
      />
      <EditPricingTierModal
        id={selectedTierId}
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          fetchPricingTiers();
        }}
      />
    </motion.div>
  );
}

export default PricingTierLayer;
